// P0 (2026-07-14) — attachment download helper.
//
// Root cause of the "Not authenticated" bug: <a href> / window.open
// navigate the browser directly, which does NOT send our in-memory
// `Authorization: Bearer <jwt>` header. This helper does a proper
// `fetch()` with the header, then triggers a client-side download via
// an ephemeral blob URL. That means EVERY attachment link in the app
// works even for guests who authenticated only via the SPA login flow.
//
// If a caller *needs* an anchor (new-tab preview, PDF-embedded link),
// use `getSignedDownloadUrl(id)` instead — it POSTs to the backend
// `/download-token` endpoint and returns a short-lived (60s) URL that
// carries a signed `?token=<jwt>` scoped to that specific attachment.
import { toast } from "sonner";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

function tokenFromStorage() {
  return localStorage.getItem("st_token") ||
         localStorage.getItem("access_token") ||
         "";
}

export async function downloadAttachment(id, filename) {
  const token = tokenFromStorage();
  try {
    const res = await fetch(`${API_ROOT}/attachments/${id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 410) {
      toast.error("This file's contents are missing. Please re-upload.");
      return false;
    }
    if (res.status === 401) {
      toast.error("Your session expired. Please log in again.");
      return false;
    }
    if (!res.ok) {
      toast.error(`Download failed: ${res.status}`);
      return false;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || `attachment-${id}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick so Safari has time to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 250);
    return true;
  } catch (e) {
    toast.error(`Download failed: ${e.message || e}`);
    return false;
  }
}

/**
 * For the rare places that MUST render an `<a href>` (e.g. new-tab
 * preview of a PDF, an <img> that inlines the blob).
 *
 * Returns an absolute URL that the browser can navigate to directly.
 * The URL carries a signed `?token=…` that:
 *   - expires in 60 seconds,
 *   - is scoped to this attachment id (can't be reused elsewhere),
 *   - never exposes the user's long-lived JWT.
 */
export async function getSignedDownloadUrl(id) {
  const token = tokenFromStorage();
  const res = await fetch(`${API_ROOT}/attachments/${id}/download-token`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`download-token failed: ${res.status} ${detail}`);
  }
  const data = await res.json();
  // Backend returns a path — join to absolute so anchor navigation works.
  const path = data.url || `/api/attachments/${id}/download?token=${data.token}`;
  return `${process.env.REACT_APP_BACKEND_URL}${path}`;
}
